import { useEffect, useState } from 'react';
import type { GameMode, GameSettings, Visibility } from '@shared/game/types';
import { Icon } from './Icon';
import { Modal } from './Modal';
import { Toggle } from './Toggle';

interface CreateRoomModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (settings: GameSettings) => void;
  busy?: boolean;
}

const DEFAULTS: GameSettings = {
  mode: 'adventure',
  effectsEnabled: true,
  maxPlayers: 4,
  visibility: 'private',
};

export function CreateRoomModal({ open, onClose, onCreate, busy }: CreateRoomModalProps) {
  const [settings, setSettings] = useState<GameSettings>(DEFAULTS);

  useEffect(() => {
    if (open) setSettings(DEFAULTS);
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Prepara tu arena"
      subtitle="Configura el protocolo y comparte el enlace. Podrás ajustarlo antes de iniciar."
      footer={
        <>
          <button type="button" className="button button--ghost" onClick={onClose}>Cancelar</button>
          <button type="button" className="button button--primary" onClick={() => onCreate(settings)} disabled={busy}>
            <Icon name="play" size={17} /> {busy ? 'Creando…' : 'Crear sala'}
          </button>
        </>
      }
    >
      <div className="choice-grid">
        {([
          {
            mode: 'adventure' as GameMode,
            title: 'Aventura',
            text: 'Portales, orbes y fracturas. Elige si quieres anomalías.',
            icon: 'spark' as const,
          },
          {
            mode: 'classic' as GameMode,
            title: 'Clásico',
            text: 'Sin anomalías. Solo reglas clásicas y campos estables.',
            icon: 'sword' as const,
          },
        ]).map((choice) => (
          <button
            type="button"
            key={choice.mode}
            className={`choice-card ${settings.mode === choice.mode ? 'is-selected' : ''}`}
            onClick={() =>
              setSettings((current) => ({
                ...current,
                mode: choice.mode,
                effectsEnabled: choice.mode === 'classic' ? false : current.effectsEnabled,
              }))
            }
          >
            <span className="choice-card__icon"><Icon name={choice.icon} size={22} /></span>
            <strong>{choice.title}</strong>
            <span>{choice.text}</span>
          </button>
        ))}
      </div>

      <div className="settings-block">
        <Toggle
          checked={settings.mode === 'adventure' && settings.effectsEnabled}
          onChange={(effectsEnabled) => setSettings((current) => ({ ...current, effectsEnabled }))}
          label="Activar anomalías"
          description="Portales, orbes sorpresa y fracturas. Si lo desactivas, Aventura usa solo las reglas clásicas."
          disabled={settings.mode === 'classic'}
        />
      </div>

      <div className="settings-block">
        <span className="settings-block__label">Jugadores máximo</span>
        <div className="segmented" role="group" aria-label="Jugadores máximo">
          {[2, 3, 4].map((count) => (
            <button
              type="button"
              key={count}
              className={settings.maxPlayers === count ? 'is-active' : ''}
              onClick={() => setSettings((current) => ({ ...current, maxPlayers: count as 2 | 3 | 4 }))}
            >
              <Icon name="users" size={16} /> {count}
            </button>
          ))}
        </div>
      </div>

      <div className="settings-block">
        <span className="settings-block__label">Visibilidad</span>
        <div className="choice-grid choice-grid--compact">
          {([
            { value: 'private' as Visibility, title: 'Privada', text: 'Solo con enlace o código.', icon: 'link' as const },
            { value: 'public' as Visibility, title: 'Pública', text: 'Aparece en el vestíbulo.', icon: 'globe' as const },
          ]).map((choice) => (
            <button
              type="button"
              key={choice.value}
              className={`choice-card choice-card--small ${settings.visibility === choice.value ? 'is-selected' : ''}`}
              onClick={() => setSettings((current) => ({ ...current, visibility: choice.value }))}
            >
              <span className="choice-card__icon"><Icon name={choice.icon} size={19} /></span>
              <strong>{choice.title}</strong>
              <span>{choice.text}</span>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}
